import React, { useState, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import {
  UserPlus,
  Bell,
  ArrowUpCircle,
  Download,
  Zap,
  CheckCircle2,
  XCircle,
  Wrench,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormService, FormField } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import { useChannelTemplates, useChannelType, useSendCommunication } from "@features/cx/consumerManagement/hooks";
import { ReassignTechnicianModal } from "../assigned/reassign/ReassignTechnicianModal";
import { SingleTechnicianAssignmentModal } from "../assigned/bulk/assignment/individualTechnician/SingleTechnicianAssignmentModal";
import { formatDate, formatDateTime, wxUtils } from "@features/wx/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { usePriority, useUpdateJobCardPriority, useAcceptJobCard, useDownloadReport } from "../../hooks";
import RejectServiceOrderModal from "../completed/view/rejectServiceOrderModal";
import CreateReworkOrderModal from "../rejected/view/reworkOrderModal";

export const QuickActions = ({ jobCard }: { jobCard?: any }) => {
  const [isUpdatePriorityOpen, setIsUpdatePriorityOpen] = useState(false);
  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isReworkModalOpen, setIsReworkModalOpen] = useState(false);
const{data:priorityData}=usePriority();
const priorityOptions = useMemo(() => {
  if (!priorityData?.result) return [];
  return priorityData.result.map((item: any) => ({
    label: item.value,
    value: String(item.key),
  }));
}, [priorityData]);
const { mutate: updatePriority, isPending: isUpdatingPriority } = useUpdateJobCardPriority();

const handleUpdatePriority = () => {
  const payload = {
    job_card_id: jobCard.id,
    priority: Number(form.getValues("priority")),
    remote_utility_id: jobCard?.remoteUtilityId || 0
  };
  updatePriority(payload, {
    onSuccess: () => setIsUpdatePriorityOpen(false)
  });
};

  const transformedData = useMemo(() => jobCard && wxUtils.transformJobCardDetail(jobCard), [jobCard]);
  const isAssigned = useMemo(() => {
    const techName = transformedData?.technicianDetails?.technicianName;
    return !!techName && techName !== "Not Assigned" && techName !== "N/A";
  }, [transformedData]);
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname.toLowerCase();

  const acceptJobCard = useAcceptJobCard();
//pending state
  const downloadCSV = useDownloadReport();

  const handleApprove = () => {
    if (!jobCard?.id) return;
    acceptJobCard.mutateAsync({
      jobCardId: parseInt(jobCard.id),
    }).then(() => {
      navigate("/service-orders/dispatcher?page=1&tab=completed");
    });
  };

  const handleReject = () => {
    if (!jobCard?.id) return;
    setIsRejectModalOpen(true);
  };

  const handleDownload = () => {
    if (!jobCard?.id) return;
    downloadCSV.mutateAsync({
      reportId: Number(jobCard.id),
      tabName: "completed",
    });
  };

  const disableDownload = !pathname.includes("completed") && !pathname.includes("rejected");
  const isCompleted = pathname.includes("completed");
  const isRejected = pathname.includes("rejected");
  const isPendingView = pathname.includes("pending");
  const serviceOrderForReassign = useMemo(() => {
    if (!jobCard) return null;
    
    return {
      id: jobCard.id,
      name: jobCard?.sop?.shortName || jobCard?.sop?.name || "N/A",
      sop: jobCard?.sop?.id || jobCard?.sop,
      technician: {
        name: jobCard?.fieldForce?.name || jobCard?.assignedFieldForce || "Unknown",
      },
      assignDate: formatDate(jobCard.assignDate),
      assignedBy: jobCard?.fieldForce?.name || jobCard?.assignedFieldForce || "Unknown",
      statusDisplay: jobCard.statusDisplay || "N/A",
      expectedEndDate: formatDateTime(jobCard.expectedEndDate),
      area: jobCard.areaName || jobCard.area || "N/A",
      subArea: jobCard.subAreaName || jobCard.subArea || "",
      latitude: jobCard.latitude,
      longitude: jobCard.longitude,
    };
  }, [jobCard]);

  const fields: FormField[] = useMemo(
    () => [
      {
        name: "priority",
        label: "Priority",
        type: "select",
        options: priorityOptions,
        defaultValue: jobCard?.priority ? priorityOptions?.find((item: any) => item.label === jobCard.priority)?.value : undefined,
        className: "col-span-2",
      },
    ],
    [priorityOptions, jobCard]
  );

  const form = FormService.createForm({ fields });
  const baseActions = [
    
    {
      label: "Assign Technician",
      icon: UserPlus,
      onClick: () => isPendingView ? setIsAssignOpen(true) : setIsReassignOpen(true),
      disabled: isCompleted,
    },
    {
      label: "Notify Technician",
      icon: Bell,
      onClick: () => setIsNotifyOpen(true),
      disabled: !isAssigned,
    },
    {
      label: "Update Priority",
      icon: ArrowUpCircle,
      onClick: () => setIsUpdatePriorityOpen(true),
    },
    {
      label: "Download Report",
      icon: Download,
      onClick: handleDownload,
      disabled: disableDownload,
      isLoading: downloadCSV.isPending,
    },
  ];

  const actions = isCompleted
    ? [
        ...baseActions.filter(a => a.label !== "Assign Technician"),
        {
          label: "Approve Service Order",
          icon: CheckCircle2,
          onClick: handleApprove,
          className: "text-[#00B050] hover:text-green-700 hover:bg-green-50",
        },
        {
          label: "Reject Service Order",
          icon: XCircle,
          onClick: handleReject,
          className: "text-red-500 hover:text-red-600 hover:bg-red-50",
        },
      ]
    : isRejected
    ? [
        ...baseActions,
        {
          label: "Create Rework Order",
          icon: Wrench,
          onClick: () => setIsReworkModalOpen(true),
          className: "text-blue-500 hover:text-blue-600 hover:bg-blue-50",
        },
      ]
    : baseActions;

  return (
    <Card className="p-4 h-full">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="h-5 w-5" />
        <h3 className="font-semibold text-lg">Quick Actions</h3>
      </div>

      <div className="space-y-3">
        {actions.map((action, index) => {
          const Icon = action.icon;

          return (
            <Button
              key={index}
              variant="ghost"
              onClick={action.onClick}
              isLoading={(action as any).isLoading}
              disabled={(action as any).disabled}
              className={`w-full justify-start gap-2 bg-white border border-gray-200 h-10 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${(action as any).className || "text-gray-700 hover:bg-gray-50"}`}
            >
              <Icon className="h-5 w-5" />
              <span className="font-medium">{action.label}</span>
            </Button>
          );
        })}
      </div>

      <Dialog open={isUpdatePriorityOpen} onOpenChange={setIsUpdatePriorityOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Priority</DialogTitle>
            <DialogDescription>
              Change the priority level for this service order. This may affect SLA calculations.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <DynamicForm
              form={form}
              fields={fields}
              onSubmit={(data) => {
             handleUpdatePriority();
              }}
              gridColumns={1}
              config={{ classes: FIELD_CLASSES }}
              renderCustomFooter={() => (
                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    className="bg-blue-700 hover:bg-blue-800 text-white"
                  >
                    Save changes
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      <ReassignTechnicianModal 
        open={isReassignOpen} 
        onOpenChange={setIsReassignOpen} 
        serviceOrder={serviceOrderForReassign as any} 
      />

      <SingleTechnicianAssignmentModal 
        open={isAssignOpen} 
        onOpenChange={setIsAssignOpen} 
        serviceOrder={serviceOrderForReassign as any} 
      />

      <CommunicationDialog
        isOpen={isNotifyOpen}
        onClose={() => setIsNotifyOpen(false)}
        consumerId={jobCard?.fieldForce?.id?.toString() || jobCard?.assignedFieldForce?.toString()}
        email={jobCard?.fieldForce?.email}
        phone={jobCard?.fieldForce?.contactNumber}
        remoteUtilityId={jobCard?.remoteUtilityId || 0}
        communicationType="NOTIFY_TECHNICIAN"
        useChannelType={useChannelType}
        useChannelTemplates={useChannelTemplates}
        useSendCommunication={useSendCommunication}
      />

      <RejectServiceOrderModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        jobCardId={jobCard?.id?.toString()}
        onSuccess={() => navigate("/service-orders/dispatcher?page=1&tab=completed")}
      />

      {jobCard && (
        <CreateReworkOrderModal
          isOpen={isReworkModalOpen}
          onClose={() => setIsReworkModalOpen(false)}
          parentOrder={{
            id: jobCard.id?.toString(),
            name: jobCard.sop?.name || jobCard.sop?.shortName || "N/A",
            rejectionReason: jobCard.rejectionReason || "N/A",
            rejectionReasonDisplay: jobCard.rejectionReasonDisplay || jobCard.rejectionReason || "N/A",
            rejectionComments: jobCard.rejectionComments || jobCard.remark || "N/A",
            rejectedBy: jobCard.rejectedBy || jobCard.rejectedByName || "N/A",
            serviceOrderData: jobCard,
          }}
        />
      )}
    </Card>
  );
};